import { useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { taskAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Sparkles, Loader2, ListTree, Target, MessageSquare, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const QUICK_ACTIONS = [
  { id: 'daily_plan', icon: Calendar, label: 'Plan my day', desc: 'Get a prioritized daily plan', color: '#14b8a6' },
  { id: 'break_into_subtasks', icon: ListTree, label: 'Break down a task', desc: 'Split a large task into smaller steps', color: '#3b82f6' },
  { id: 'suggest_priority', icon: Target, label: 'Suggest priorities', desc: 'AI recommends task priorities', color: '#f59e0b' },
  { id: 'summarize_client', icon: MessageSquare, label: 'Summarize status', desc: 'Get a client/project summary', color: '#8b5cf6' },
];

export default function TaskAIPage() {
  const { currentWorkspace } = useWorkspace();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const runAction = async (action, customQuery) => {
    if (!currentWorkspace?.id) return;
    const q = customQuery || query;
    if (!q && action !== 'daily_plan') return toast.error('Enter a query or task details');
    setLoading(true);
    try {
      const res = await taskAPI.aiAssist({ workspace_id: currentWorkspace.id, action, query: q });
      setResults(prev => [{ action, query: q, result: res.data.result, time: new Date().toLocaleTimeString() }, ...prev]);
      setQuery('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI request failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-8 space-y-6" data-testid="task-ai-page">
      <div>
        <h1 className="text-2xl font-display font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" /> AI Assistant
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Get AI-powered help with your tasks and projects.</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        {QUICK_ACTIONS.map(a => (
          <button
            key={a.id}
            onClick={() => runAction(a.id, a.id === 'daily_plan' ? 'Plan my day based on current tasks' : '')}
            disabled={loading}
            className="p-4 rounded-xl border border-border/50 bg-card/40 hover:bg-secondary/20 hover:border-border transition-all text-left group"
            data-testid={`ai-action-${a.id}`}
          >
            <a.icon className="h-5 w-5 mb-2" style={{ color: a.color }} />
            <p className="text-[13px] font-semibold">{a.label}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{a.desc}</p>
          </button>
        ))}
      </div>

      {/* Free query */}
      <div className="space-y-2">
        <Textarea
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Ask anything... e.g. 'What's pending for Client X?' or 'Break down: Create Q2 marketing campaign'"
          className="min-h-[80px] text-[13px]"
          data-testid="ai-query-input"
        />
        <div className="flex gap-2">
          <Button onClick={() => runAction('summarize_client')} disabled={loading || !query.trim()} className="flex-1" data-testid="ai-send-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Ask AI
          </Button>
        </div>
      </div>

      <Separator />

      {/* Results */}
      <ScrollArea className={results.length > 3 ? 'h-[400px]' : ''}>
        <div className="space-y-4">
          {results.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="h-10 w-10 text-muted-foreground/15 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Ask a question or use a quick action to get started.</p>
            </div>
          ) : results.map((r, i) => (
            <Card key={i} className="bg-card/60 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[11px] text-muted-foreground capitalize">{r.action.replace(/_/g, ' ')}</span>
                  <span className="text-[10px] text-muted-foreground/40 ml-auto">{r.time}</span>
                </div>
                {r.query && <p className="text-[11px] text-muted-foreground mb-2 italic">"{r.query}"</p>}
                <div className="text-[13px] leading-relaxed whitespace-pre-wrap">{r.result}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
